import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { noteKeys } from './keys.ts'

/**
 * Note hooks. The server has already dropped notes in categories this user
 * cannot read, so nothing here filters by permission — a client-side filter
 * would imply the data was ever sent, and it never is.
 */

export function useNotes(personId: string) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: noteKeys.list(personId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.notes.get({
        query: { personId, ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    notes: query.data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isLoading: query.isPending,
    isError: query.isError,
  }
}

export type Note = ReturnType<typeof useNotes>['notes'][number]

export function useNoteCategories() {
  const api = useApi()
  return useQuery({
    queryKey: noteKeys.categories(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.notes.categories.get({ query: { limit: 200 } })
      if (error) throw error
      return data.items
    },
  })
}

export type NoteCategory = NonNullable<ReturnType<typeof useNoteCategories>['data']>[number]

function useNoteInvalidation(personId: string) {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: noteKeys.list(personId) })
}

export function useCreateNote(personId: string) {
  const api = useApi()
  const invalidate = useNoteInvalidation(personId)
  return useMutation({
    mutationFn: async (input: { body: string; categoryId?: string | null; pinned?: boolean }) => {
      const { data, error } = await api.api.v1.notes.post({ personId, ...input })
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateNote(personId: string) {
  const api = useApi()
  const invalidate = useNoteInvalidation(personId)
  return useMutation({
    mutationFn: async ({
      noteId,
      ...body
    }: {
      noteId: string
      body?: string
      categoryId?: string
      pinned?: boolean
    }) => {
      const { data, error } = await api.api.v1.notes({ id: noteId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteNote(personId: string) {
  const api = useApi()
  const invalidate = useNoteInvalidation(personId)
  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await api.api.v1.notes({ id: noteId }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
}

/** Category management (people:admin, org-wide) — the settings screen. */
export function useNoteCategoryMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: noteKeys.all() })
  }

  const create = useMutation({
    mutationFn: async (body: Parameters<typeof api.api.v1.notes.categories.post>[0]) => {
      const { data, error } = await api.api.v1.notes.categories.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({
      categoryId,
      ...body
    }: {
      categoryId: string
      name?: string
      requiredPermission?: 'people:read' | 'people:write' | 'people:admin'
    }) => {
      const { data, error } = await api.api.v1.notes.categories({ id: categoryId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await api.api.v1.notes.categories({ id: categoryId }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { create, update, remove }
}
